// api/disclosure.js
// Client-held disclosure store + evidence upload (handoff 10, §3 / §6).
//
// TWO STORES, never merged:
//  - inputs  (loadDisc/saveDisc): the operator's own disclosure facts (entity,
//    period, governance narrative, targets, policies, operational emissions).
//  - lifts   (loadLifts/saveLift/clearLift): operator Lift attestations that
//    move a Baseline framework to Documented. { [fwKey]: { rep, evidence, at } }.
//
// PERSISTENCE (handoff 10 §3): both stores are now SERVER-SIDE behind the
// report-pipeline auth (`GET/PUT /api/v1/properties/:id/disclosure`, one mutable
// `farms/{id}/disclosureInputs/current` doc, etag-guarded). localStorage is kept
// as a SYNCHRONOUS CACHE so the cards' existing sync reads (loadDisc/loadLifts)
// keep working: every change writes localStorage immediately (optimistic) AND
// schedules a debounced PUT of the assembled doc; `hydrateDisclosure(farmId)`
// pulls the server doc into the cache on mount. Server failures never break the
// UI (localStorage remains the durable cache and the next change retries).
//
// The single server doc carries the operator's facts + an `attestations` map
// (the Lift store, keyed by framework id, §3c). We map the local lift shape
// { rep, evidence, at } <-> the server attestation shape { framework_id,
// representation, evidence, lifted_at } at this boundary so the cards are
// untouched.
//
// EcoIntel never publishes or shares these client-held facts. Status is always
// DERIVED (never stored here): a Lift is an INPUT; RB owns the OUTPUT status.

import {
  uploadDisclosureEvidence as uploadEvidenceApi,
  fetchDisclosureInputs,
  saveDisclosureInputs,
} from '../api';
import { readFrameworkSelection } from '../constants/disclosure';

const inputsKey = (farmId) => `ei.disclosure.inputs.${farmId || 'default'}`;
const liftsKey = (farmId) => `ei.disclosure.lifts.${farmId || 'default'}`;
const introKey = (farmId) => `ei.disclosure.introDismissed.${String(farmId || 'default').toUpperCase()}`;

// Server etag per property (in-memory; refreshed by hydrate + each successful PUT).
const _etag = {};
// Debounced PUT timers per property.
const _putTimer = {};

// Lift store shape <-> server attestations shape.
function liftsToAttestations(lifts) {
  const out = {};
  Object.keys(lifts || {}).forEach((fw) => {
    const l = lifts[fw] || {};
    out[fw] = {
      framework_id: fw,
      representation: l.rep || '',
      evidence: Array.isArray(l.evidence) ? l.evidence : [],
      lifted_at: l.at || null,
    };
  });
  return out;
}
function attestationsToLifts(att) {
  const out = {};
  Object.keys(att || {}).forEach((fw) => {
    const a = att[fw] || {};
    out[fw] = {
      rep: a.representation || '',
      evidence: Array.isArray(a.evidence) ? a.evidence : [],
      at: a.lifted_at || null,
    };
  });
  return out;
}

// Assemble the full server doc from the two localStorage stores + last etag.
function assembleServerDoc(farmId) {
  const inputs = loadDisc(farmId) || {};
  // Never let cached server-managed keys leak back into the body.
  const { attestations, etag, updatedAt, createdAt, updatedByUid, ...rest } = inputs;
  const body = {
    ...rest,
    attestations: liftsToAttestations(loadLifts(farmId)),
  };
  if (_etag[farmId]) body.etag = _etag[farmId];
  return body;
}

// Push the assembled doc to the server (debounced entry point below). One
// stale-etag retry: re-hydrate to refresh the etag + server state, then re-PUT.
async function pushToServer(farmId, retried = false) {
  try {
    const res = await saveDisclosureInputs(farmId, assembleServerDoc(farmId));
    if (res && res.etag) _etag[farmId] = res.etag;
  } catch (e) {
    if (e && e.code === 'stale_etag' && !retried) {
      if (e.etag) _etag[farmId] = e.etag;
      await hydrateDisclosure(farmId);
      return pushToServer(farmId, true);
    }
    // Network / not-yet-provisioned: keep the localStorage cache; next change retries.
  }
  return undefined;
}

function scheduleServerSync(farmId) {
  if (_putTimer[farmId]) clearTimeout(_putTimer[farmId]);
  _putTimer[farmId] = setTimeout(() => { void pushToServer(farmId); }, 600);
}

// Pull the server doc into the localStorage cache on mount. Returns true when a
// server doc was applied. Best-effort: on failure the localStorage cache stands.
export async function hydrateDisclosure(farmId) {
  try {
    const doc = await fetchDisclosureInputs(farmId);
    if (doc && typeof doc === 'object' && Object.keys(doc).length) {
      const {
        attestations, etag, updatedAt, createdAt, updatedByUid, ...inputs
      } = doc;
      writeJSON(inputsKey(farmId), { ...inputs, _updated_at: updatedAt || inputs._updated_at || null });
      writeJSON(liftsKey(farmId), attestationsToLifts(attestations));
      if (etag) _etag[farmId] = etag;
      return true;
    }
  } catch (_) {
    /* offline / endpoint unavailable: keep the localStorage cache */
  }
  return false;
}

function readJSON(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

function writeJSON(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (_) {
    /* storage unavailable: state simply won't persist this session */
  }
}

// ---- inputs store --------------------------------------------------------

// Return the nested inputs object for a property ({} when nothing stored).
export function loadDisc(farmId) {
  const v = readJSON(inputsKey(farmId));
  return v && typeof v === 'object' ? v : {};
}

// Persist the whole nested inputs object (InputsCard rebuilds + setDeeps a path,
// stamps _updated_at, then calls this). Writes the localStorage cache immediately
// and schedules a debounced server PUT of the assembled doc.
export async function saveDisc(farmId, obj) {
  writeJSON(inputsKey(farmId), obj || {});
  scheduleServerSync(farmId);
  return obj || {};
}

// ---- lift store ----------------------------------------------------------

// Return the lift store for a property ({} when nothing stored / un-lifted).
export function loadLifts(farmId) {
  const v = readJSON(liftsKey(farmId));
  return v && typeof v === 'object' ? v : {};
}

// Record a Lift: attach a management representation + evidence refs to a
// framework key. Baseline -> Documented ONLY (the status flip is derived on the
// next resolveFrameworks pass; this only stores the INPUT).
export async function saveLift(farmId, fwKey, rep, evidence) {
  const store = loadLifts(farmId);
  store[fwKey] = {
    rep: String(rep || '').trim(),
    evidence: Array.isArray(evidence) ? evidence : [],
    at: new Date().toISOString(),
  };
  writeJSON(liftsKey(farmId), store);
  scheduleServerSync(farmId);
  return store[fwKey];
}

// Remove a Lift (Undo): reverts Documented -> Baseline on the next resolve.
export async function clearLift(farmId, fwKey) {
  const store = loadLifts(farmId);
  if (store[fwKey]) {
    delete store[fwKey];
    writeJSON(liftsKey(farmId), store);
    scheduleServerSync(farmId);
  }
  return store;
}

// ---- intro banner dismissal (handoff 12 §D1) -----------------------------

// The closable disclosure intro banner is dismissed per-property and stays
// gone on re-render / re-visit. Persisted to localStorage, matching the RB-UX
// key shape (`ei.disclosure.introDismissed.<CODE>`).
export function isIntroDismissed(farmId) {
  try {
    return localStorage.getItem(introKey(farmId)) === '1';
  } catch (_) {
    return false;
  }
}

export function dismissIntro(farmId) {
  try {
    localStorage.setItem(introKey(farmId), '1');
  } catch (_) {
    /* storage unavailable: banner will simply reappear next visit */
  }
}

// ---- framework selection (per-property, client-held) ---------------------
// Stored on the same disclosureInputs doc as operator facts. Portfolio
// settings will override `enabledFrameworks` later; keep the field name stable.

const _selectionListeners = new Set();

export function subscribeDisclosureSelection(listener) {
  if (typeof listener !== 'function') return () => {};
  _selectionListeners.add(listener);
  return () => { _selectionListeners.delete(listener); };
}

function emitDisclosureSelection(farmId) {
  _selectionListeners.forEach((fn) => {
    try { fn(farmId); } catch (err) {
      console.error('[disclosure] selection listener failed', err);
    }
  });
}

export function loadFrameworkSelection(farmId) {
  return readFrameworkSelection(loadDisc(farmId));
}

export async function saveFrameworkSelection(farmId, enabledKeys) {
  const obj = loadDisc(farmId) || {};
  const known = Array.isArray(enabledKeys) ? enabledKeys.map((k) => String(k)) : [];
  obj.enabledFrameworks = known;
  obj.frameworksChosenAt = new Date().toISOString();
  obj._updated_at = obj.frameworksChosenAt;
  await saveDisc(farmId, obj);
  emitDisclosureSelection(farmId);
  return obj;
}

export async function saveFrameworkSuggestion(farmId, { name, note, uid }) {
  const obj = loadDisc(farmId) || {};
  const list = Array.isArray(obj.frameworkSuggestions) ? obj.frameworkSuggestions.slice() : [];
  const entry = {
    name: String(name || '').trim(),
    note: String(note || '').trim(),
    at: new Date().toISOString(),
    uid: uid || null,
  };
  if (!entry.name) return null;
  list.push(entry);
  obj.frameworkSuggestions = list;
  obj._updated_at = entry.at;
  await saveDisc(farmId, obj);
  return entry;
}

// ---- evidence upload -----------------------------------------------------

// Upload Lift evidence to the per-property private store, returning [{name,path}].
// Thin wrapper over the api.js network call; on failure the caller keeps a
// filename-only fallback so the Lift still records the representation.
export async function uploadDisclosureEvidence(farmId, fwKey, filesPayload) {
  return uploadEvidenceApi(farmId, fwKey, filesPayload);
}
